import {
  isEmpty,
  isNil,
  pick,
  snakeCase
} from 'lodash';

import {
  getCommonValues,
  getSelectedItems,
  getValues,
  joinItems
} from 'modules/discovery/utils/tracking';
import {
  ENGAGEMENT,
  FOLLOWERS,
  GENDER_RATIO,
  VIEWS
} from 'modules/discovery/creators/constants/filters';
import { differenceBetweenObjects } from 'utils/helper/general';

import type {
  DiscoveryCreatorsFilter,
  DiscoveryCreatorsFilterEngagement,
  DiscoveryCreatorsFilterFollowers,
  DiscoveryCreatorsFilterKeywords,
  DiscoveryCreatorsFilterViews,
  DiscoveryCreatorsTracking
} from 'types';
import { DiscoveryCreatorsFilterChangeAction } from 'modules/tracking/types/user-actions';

type RangeAlias = { min: number, max: number };

const getUpdatedKeywordValues = (currentFilter: DiscoveryCreatorsFilterKeywords): DiscoveryCreatorsTracking => ({
  keywords: {
    included: joinItems(currentFilter.included),
    excluded: joinItems(currentFilter.excluded),
    matchMode: currentFilter.matchMode
  }
});

export const getUpdatedFollowersValues = (currentFilter: DiscoveryCreatorsFilterFollowers): DiscoveryCreatorsFilter => {
  const instagram = currentFilter.instagram;
  const youtube = currentFilter.youtube;
  const tiktok = currentFilter.tiktok;

  const transformed = {
    instagram: instagram.transformed,
    youtube: youtube.transformed,
    tiktok: tiktok.transformed
  };

  const selectedTiers = getSelectedItems(instagram.tiers?.data || []);

  return {
    followers: {
      ...currentFilter,
      instagram: {
        min: transformed.instagram?.min,
        max: transformed.instagram?.max,
        tiers: instagram.tiers?.enabled ? joinItems(getValues(selectedTiers)) : ''
      },
      youtube: {
        min: transformed.youtube?.min,
        max: transformed.youtube?.max
      },
      tiktok: {
        min: transformed.tiktok?.min,
        max: transformed.tiktok?.max
      }
    }
  };
};

const getUpdatedEngagementValues = (currentFilter: DiscoveryCreatorsFilterEngagement): DiscoveryCreatorsFilter => {
  const instagram = currentFilter.instagram;
  const youtube = currentFilter.youtube;
  const tiktok = currentFilter.tiktok;

  return {
    engagement: {
      instagram: {
        min: instagram.min,
        max: instagram.max
      },
      youtube: {
        min: youtube.min,
        max: youtube.max
      },
      tiktok: {
        min: tiktok.min,
        max: tiktok.max
      }
    }
  };
};

const getUpdatedViewsValues = (currentFilter: DiscoveryCreatorsFilterViews): DiscoveryCreatorsFilter => {
  const views = currentFilter.youtube.transformed;
  const viewsTikTok = currentFilter.tiktok.transformed;

  return {
    views: {
      min: views?.min,
      max: views?.max
    },
    viewsTikTok: {
      min: viewsTikTok?.min,
      max: viewsTikTok?.max
    }
  };
};

const getInstagramTikTokOrYoutubeFollowers = (
  filter: { instagram: RangeAlias & { tiers: string }, tiktok: RangeAlias, youtube: RangeAlias }
): Partial<{ instagram: RangeAlias & { tiers: string }, tiktok: RangeAlias, youtube: RangeAlias }> => {
  if (
    filter.instagram.min !== FOLLOWERS.MIN || filter.instagram.max !== FOLLOWERS.MAX
    || !!filter.instagram.tiers
  ) {
    return {
      instagram: filter.instagram
    };
  }

  if (filter.tiktok.min !== FOLLOWERS.MIN || filter.tiktok.max !== FOLLOWERS.MAX) {
    return {
      tiktok: filter.tiktok
    };
  }

  return {
    youtube: filter.youtube
  };
};

const getInstagramTikTokOrYoutubeEngagement = (
  filter: { instagram: RangeAlias, tiktok: RangeAlias, youtube: RangeAlias }
): Partial<{ instagram: RangeAlias, tiktok: RangeAlias, youtube: RangeAlias }> => {
  if (
    filter.instagram.min !== ENGAGEMENT.MIN_INSTAGRAM
    || filter.instagram.max !== ENGAGEMENT.MAX_INSTAGRAM
  ) {
    return {
      instagram: filter.instagram
    };
  }

  if (
    filter.tiktok.min !== ENGAGEMENT.MIN_REST
    || filter.tiktok.max !== ENGAGEMENT.MAX_REST
  ) {
    return {
      tiktok: filter.tiktok
    };
  }

  return {
    youtube: filter.youtube
  };
};

const getTikTokOrYoutube = (
  filter: { views: RangeAlias, viewsTikTok: RangeAlias }
): Partial<{ viewsTikTok: RangeAlias, views: RangeAlias }> => {
  if (filter.viewsTikTok.min !== VIEWS.MIN || filter.viewsTikTok.max !== VIEWS.MAX) {
    return {
      viewsTikTok: filter.viewsTikTok
    };
  }

  return {
    views: filter.views
  };
};

const getUpdatedPartByComparisonWithCachedValue = (() => {
  const previousFiltersCache = {};
  const previousDifferenceCache = {};
  return (key: string, filters: any): object => {
    const cached = previousFiltersCache[key];
    previousFiltersCache[key] = filters;
    let diff;

    if (key === 'followers') {
      if (isNil(cached)) {
        diff = getInstagramTikTokOrYoutubeFollowers(filters[key]);
      } else {
        diff = differenceBetweenObjects(filters[key], cached[key]);
      }
    } else if (key === 'engagement') {
      if (isNil(cached)) {
        diff = getInstagramTikTokOrYoutubeEngagement(filters[key]);
      } else {
        diff = differenceBetweenObjects(filters[key], cached[key]);
      }
    } else if (key === 'views') {
      if (isNil(cached)) {
        diff = getTikTokOrYoutube(filters);
      } else {
        diff = differenceBetweenObjects(filters, cached);
      }
    } else if (key === 'audienceGenderRatio' || key === 'audienceGenderRatioTikTok') {
      if (isNil(cached)) {
        if (filters.male.min !== GENDER_RATIO.MIN || filters.male.max !== GENDER_RATIO.MAX) {
          diff = {
            male: filters.male
          };
        } else {
          diff = {
            female: filters.female
          };
        }
      } else {
        diff = differenceBetweenObjects(filters, cached);
      }
    }

    if (isNil(diff) || isEmpty(diff)) {
      diff = previousDifferenceCache[key];
    } else {
      previousDifferenceCache[key] = diff;
    }

    return diff;
  };
})();

export const prepareFiltersForAnalytics = (updatedFilters: any): DiscoveryCreatorsFilterChangeAction => {
  const getRange = <T extends { min: number, max: number }>(object: T): string => (
    `${object.min} to ${object.max}`
  );
  const currentFilterName = Object.keys(updatedFilters)[0];
  const currentFilter = updatedFilters[currentFilterName];
  const standardPropsWithSelectedItems = [
    'channels',
    'bodyAttributes',
    'ethnicGroups',
    'genders',
    'professions',
    'profiles',
    'styles'
  ];
  const eventCategory = 'discovery creators filters';

  if (standardPropsWithSelectedItems.includes(currentFilterName)) {
    return {
      eventCategory,
      eventAction: snakeCase(currentFilterName).replace('_', ' ') as any,
      eventLabel: getCommonValues(currentFilter, currentFilterName)[currentFilterName].split(',')
    };
  }

  if (currentFilterName === 'categories') {
    return {
      eventCategory,
      eventAction: 'categories' as any,
      eventLabel: {
        included: getCommonValues(currentFilter.items, currentFilterName)[currentFilterName].split(','),
        excluded: [],
        matchMode: currentFilter.matchMode
      }
    };
  }

  if (currentFilterName === 'searchByName') {
    return {
      eventCategory,
      eventAction: 'search by name',
      eventLabel: currentFilter
    };
  }

  if (currentFilterName === 'audienceAges' || currentFilterName === 'audienceAgesTikTok') {
    return {
      eventCategory,
      eventAction: currentFilterName === 'audienceAges'
        ? 'instagram audience age'
        : 'tiktok audience age',
      eventLabel: getCommonValues(currentFilter, currentFilterName)[currentFilterName].split(',')
    };
  }

  if (
    currentFilterName === 'audienceAgesShare' || currentFilterName === 'audienceAgesShareTikTok'
  ) {
    return {
      eventCategory,
      eventAction: currentFilterName === 'audienceAgesShare'
        ? 'instagram audience age share'
        : 'tiktok audience age share',
      eventLabel: currentFilter.value
    };
  }

  if (
    currentFilterName === 'audienceCountries' || currentFilterName === 'audienceCountriesTikTok'
  ) {
    return {
      eventCategory,
      eventAction: currentFilterName === 'audienceCountries'
        ? 'instagram audience countries'
        : 'tiktok audience countries',
      eventLabel: currentFilter.map(v => v.label)
    };
  }

  if (
    currentFilterName === 'audienceCountriesShare'
    || currentFilterName === 'audienceCountriesShareTikTok'
  ) {
    return {
      eventCategory,
      eventAction: currentFilterName === 'audienceCountriesShare'
        ? 'instagram audience countries share'
        : 'tiktok audience countries share',
      eventLabel: currentFilter.value
    };
  }

  if (currentFilterName === 'audienceCities') {
    return {
      eventCategory,
      eventAction: 'instagram audience cities',
      eventLabel: currentFilter.map(v => v.label)
    };
  }

  if (
    currentFilterName === 'audienceReachability'
    || currentFilterName === 'audienceReachabilityTikTok'
  ) {
    return {
      eventCategory,
      eventAction: currentFilterName === 'audienceReachability'
        ? 'instagram audience reachability'
        : 'tiktok audience reachability',
      eventLabel: getCommonValues(currentFilter, currentFilterName)[currentFilterName].split(',')
    };
  }

  if (currentFilterName === 'audienceCredibility' || currentFilterName === 'audienceInterests') {
    return {
      eventCategory,
      eventAction: currentFilterName === 'audienceCredibility'
        ? 'instagram audience credibility'
        : 'instagram audience interests',
      eventLabel: getCommonValues(currentFilter, currentFilterName)[currentFilterName].split(',')
    };
  }

  if (
    currentFilterName === 'audienceGenderRatio'
    || currentFilterName === 'audienceGenderRatioTikTok'
  ) {
    const updated = getUpdatedPartByComparisonWithCachedValue(currentFilterName, currentFilter);
    const updatedKey = Object.keys(updated)[0];
    const eventLabel = getRange(updated[updatedKey]);
    let eventAction;

    if (currentFilterName === 'audienceGenderRatio') {
      eventAction = updatedKey === 'male'
        ? 'instagram audience male gender ratio'
        : 'instagram audience female gender ratio';
    } else {
      eventAction = updatedKey === 'male'
        ? 'tiktok audience male gender ratio'
        : 'tiktok audience female gender ratio';
    }

    return {
      eventCategory,
      eventAction,
      eventLabel
    };
  }

  if (currentFilterName === 'followers') {
    const updated = getUpdatedPartByComparisonWithCachedValue(
      currentFilterName, getUpdatedFollowersValues(currentFilter)
    );
    const updatedKey = Object.keys(updated)[0];
    let eventAction;
    let eventLabel;

    if (updatedKey === 'instagram') {
      eventAction = 'instagram followers';

      if (updated[updatedKey].tiers) {
        eventLabel = updated[updatedKey].tiers
          .split(',')
          .map(v => {
            const match = v.match(/^(.*):/) ?? [];
            return match[1] || '';
          })
          .filter(v => !!v);
      } else {
        eventLabel = [getRange(updated[updatedKey])];
      }
    } else {
      eventAction = updatedKey === 'tiktok' ? 'tiktok followers' : 'youtube followers';
      eventLabel = [getRange(updated[updatedKey])];
    }

    return {
      eventCategory,
      eventAction,
      eventLabel
    };
  }

  if (currentFilterName === 'engagement') {
    const updated = getUpdatedPartByComparisonWithCachedValue(
      currentFilterName, getUpdatedEngagementValues(currentFilter)
    );
    const updatedKey = Object.keys(updated)[0];
    const eventLabel = getRange(updated[updatedKey]);
    let eventAction;

    if (updatedKey === 'instagram') {
      eventAction = 'instagram engagement';
    } else if (updatedKey === 'tiktok') {
      eventAction = 'tiktok engagement';
    } else {
      eventAction = 'youtube engagement';
    }

    return {
      eventCategory,
      eventAction,
      eventLabel
    };
  }

  if (currentFilterName === 'keywords') {
    const values: any = getUpdatedKeywordValues(currentFilter).keywords;

    return {
      eventCategory,
      eventAction: 'keywords',
      eventLabel: {
        excluded: values.excluded.split(','),
        included: values.included.split(','),
        matchMode: values.matchMode
      }
    };
  }

  if (currentFilterName === 'views') {
    const updated = getUpdatedPartByComparisonWithCachedValue(currentFilterName, getUpdatedViewsValues(currentFilter));
    const updatedKey = Object.keys(updated)[0];
    const eventLabel = getRange(updated[updatedKey]);
    const eventAction = updatedKey === 'views' ? 'youtube views' : 'tiktok views';

    return {
      eventCategory,
      eventAction,
      eventLabel
    };
  }

  if (currentFilterName === 'cities' || currentFilterName === 'countries') {
    const eventAction = currentFilterName === 'countries' ? 'country' : 'city';

    return {
      eventCategory,
      eventAction,
      eventLabel: currentFilter.map(v => v.label)
    };
  }

  if (currentFilterName === 'age') {
    return {
      eventCategory,
      eventAction: 'age',
      eventLabel: getRange(pick(currentFilter, ['min', 'max']))
    };
  }

  throw new Error('Unhandled filter in creator discovery!');
};
